import { useCallback, useEffect, useMemo, useState } from 'react';
import { entries, mergeWith, orderBy, sum } from 'lodash';
import { useToast } from '@chakra-ui/react';
import { useGetRetailerByClientQuery } from 'core/API/authApi';
import { useGetFacByRetailerQuery, useUpdateFacultativeNameSplitsMutation } from 'core/API/productsApi';
import { useTranslation } from 'helpers/languages/translations';
import { IRetailer } from 'shared/types';
import { useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';

const useFacultativeContainer = () => {
    const translate = useTranslation();
    const toast = useToast();
    const [updateFacSplit, { isLoading: isFacLoading }] = useUpdateFacultativeNameSplitsMutation();
    const { data: retailerResp } = useGetRetailerByClientQuery();
    const { data: facResp } = useGetFacByRetailerQuery();

    const retailers = orderBy(retailerResp?.data, 'name');
    const facByRet = facResp?.data || {};

    const [checkedList, setCheckList] = useState<Record<string, number[]>>({});
    const [hasChanges, setHasChanges] = useState(false);
    const [selectedRetailers, setSelectedRetailers] = useState<IRetailer[]>([]);

    const userInfo = useGetUserInfoSelector();
    const readonly = !userInfo?.user_groups?.includes(20);

    useEffect(() => {
        const changes = entries(checkedList).filter(([ret, fac]) => sum(facByRet[ret]) !== sum(fac)).length;
        setHasChanges(!!changes);
    }, [checkedList]);

    useEffect(() => {
        setCheckList(facResp?.data || {});
        setSelectedRetailers(retailers.filter((ret) => facByRet[ret.id]?.length));
    }, [facResp?.data]);

    const handleCheck = useCallback(
        (retailerId: number | string, facultative: number[]) => {
            setCheckList((list) => ({ ...list, [retailerId]: facultative }));
        },
        [setCheckList, mergeWith],
    );

    const handleUpdateFacByRetailer = async () => {
        const promises = entries(checkedList).map(([retailer, fac]) => {
            const hasChange = sum(facByRet[retailer]) !== sum(fac);

            return hasChange
                ? updateFacSplit({
                      retailer,
                      lang: 'default',
                      fac,
                  })
                : false;
        });

        setHasChanges(false);

        const resps = await Promise.all(promises);
        const isError = resps.filter((resp: any) => resp?.error?.code).length;

        resps.length &&
            toast({
                title: translate(`splits.fac.${isError ? 'error' : 'success'}`),
                status: isError ? 'error' : 'success',
            });
    };

    const addSelectedRetailer = (retailerId: number) => {
        const newRet = retailers.find((retailer) => retailer.id === retailerId);
        newRet && setSelectedRetailers((sr) => [...sr, newRet]);
    };

    const allRetailers = useMemo(
        () => retailers.filter((ret) => !selectedRetailers.find((sr) => sr.id === ret.id)),
        [retailers, facByRet],
    );

    const props = useMemo(
        () => ({
            allRetailers,
            retailers: selectedRetailers,
            checkedList,
            hasChanges,
            readonly,
            addSelectedRetailer,
            setCheckList,
            handleCheck,
            handleUpdateFacByRetailer,
        }),
        [retailers, checkedList, hasChanges, readonly],
    );

    return props;
};

export type TFacultativeTabProps = ReturnType<typeof useFacultativeContainer>;
export default useFacultativeContainer;
